/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * sanitizeRichText
 * ----------------
 * An allowlist for the HTML an organizer can put on a public page.
 *
 * Event and activity descriptions are rich text, and they are written by
 * organizers rather than administrators. A description is therefore treated the
 * same way any other untrusted input is: parsed into a detached document, walked
 * node by node, and rebuilt from what is explicitly permitted. Nothing here
 * matches markup with a regex — the attack on that approach is well known and
 * the parser is already in the browser.
 *
 * Typography arrives as class names, never inline CSS. That means this file
 * allowlists eight literal strings instead of parsing style declarations, and
 * `styles/eventDetail.css` keeps control of what each one looks like.
 *
 * Server-side twin: `prerender_views._plain_text` in arcee, which strips tags
 * before a description reaches a share card.
 */

/** Tags that may appear in a stored description. */
const ALLOWED_TAGS = new Set([
  'P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'S',
  'H3', 'H4', 'UL', 'OL', 'LI', 'A', 'SPAN',
]);

/**
 * Tags whose CONTENTS go with them.
 *
 * Everything else that is not allowed is unwrapped — the tag goes, the words
 * stay — because an organizer who pastes from a word processor should not lose
 * a paragraph to a stray <div>. For these four, the contents are the payload.
 */
const DROP_WITH_CONTENTS = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'TEMPLATE']);

export const FONT_CLASSES = ['rt-serif', 'rt-mono'];
export const SIZE_CLASSES = ['rt-sm', 'rt-lg', 'rt-xl'];
export const COLOR_CLASSES = ['rt-accent', 'rt-muted', 'rt-go'];

/**
 * The whole typography surface. A class not in here cannot be stored.
 *
 * The order matters: it is the nesting order of the canonical form below.
 */
export const CLASS_ORDER = [...FONT_CLASSES, ...SIZE_CLASSES, ...COLOR_CLASSES];
export const ALLOWED_CLASSES = new Set(CLASS_ORDER);

const SAFE_HREF = /^(https?:|mailto:)/i;

/** Parses into a detached document that never runs anything it contains. */
function parse(html) {
  return new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
}

function cleanElement(element, doc) {
  const tag = element.tagName;

  if (DROP_WITH_CONTENTS.has(tag)) {
    element.remove();
    return;
  }

  // Not allowed, but the words are the organizer's: unwrap rather than delete.
  if (!ALLOWED_TAGS.has(tag)) {
    cleanChildren(element, doc);
    element.replaceWith(...element.childNodes);
    return;
  }

  const classes = [...element.classList].filter((name) => ALLOWED_CLASSES.has(name));

  [...element.attributes].forEach((attr) => {
    const name = attr.name.toLowerCase();
    if (name === 'class') return;                 // reinstated below, from the allowlist
    if (tag === 'A' && name === 'href') {
      if (!SAFE_HREF.test(attr.value.trim())) element.removeAttribute('href');
      return;
    }
    // Every on*, every style, every data-*, every src, everything else.
    element.removeAttribute(attr.name);
  });

  if (classes.length) element.setAttribute('class', classes.join(' '));

  // A span carrying no token is noise in the stored string, and noise in a
  // stored string is what a diff has to read past a year from now.
  if (tag === 'SPAN' && !classes.length) {
    cleanChildren(element, doc);
    element.replaceWith(...element.childNodes);
    return;
  }

  // ── One token per span, nested ─────────────────────────────────────────
  // This is the canonical form, and it is not cosmetic. ProseMirror applies at
  // most ONE mark rule per DOM element, so `<span class="rt-mono rt-lg">` loads
  // into the editor as mono only — the organizer reopens their event and the
  // size they chose is silently gone. Nesting is also exactly what TipTap emits
  // when several marks are active, so a value round-trips unchanged.
  if (tag === 'SPAN' && classes.length > 1) {
    const ordered = CLASS_ORDER.filter((name) => classes.includes(name));
    const outer = doc.createElement('span');
    outer.setAttribute('class', ordered[0]);
    let innermost = outer;
    ordered.slice(1).forEach((name) => {
      const next = doc.createElement('span');
      next.setAttribute('class', name);
      innermost.appendChild(next);
      innermost = next;
    });
    innermost.append(...element.childNodes);
    element.replaceWith(outer);
    cleanChildren(innermost, doc);
    return;
  }

  if (tag === 'A') {
    element.setAttribute('target', '_blank');
    element.setAttribute('rel', 'noopener noreferrer nofollow');
  }

  cleanChildren(element, doc);
}

function cleanChildren(node, doc) {
  [...node.childNodes].forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) return;      // text is text
    if (child.nodeType !== Node.ELEMENT_NODE) {          // comments, CDATA, the rest
      child.remove();
      return;
    }
    cleanElement(child, doc);
  });
}

/**
 * The description as it is safe to render.
 *
 * @param {string} html - a stored description, rich or legacy plain text
 * @returns {string} HTML containing only allowlisted tags, classes and hrefs
 */
export function sanitizeRichText(html) {
  if (typeof html !== 'string' || html === '') return '';
  const doc = parse(html);
  cleanChildren(doc.body, doc);
  return doc.body.innerHTML;
}

/**
 * The words, without the markup.
 *
 * This is what a character cap must measure: markup is not something an
 * organizer typed, and counting it would shrink their budget every time they
 * made a word bold.
 *
 * @param {string} html
 * @returns {string}
 */
export function richTextToPlain(html) {
  if (typeof html !== 'string' || html === '') return '';
  const doc = parse(html);
  // Script and style text is not content; it would otherwise be counted and,
  // worse, read as if the organizer had written it.
  doc.body.querySelectorAll('script, style').forEach((node) => node.remove());
  return doc.body.textContent || '';
}

/**
 * Whether a stored value is rich text or the plain text that predates it.
 *
 * Deliberately narrow: it looks for a real tag, so a description reading
 * "Pace < 5:00 per km" stays plain text and keeps rendering exactly as it does
 * today. Getting this wrong in the other direction would silently change how
 * every existing event reads.
 *
 * @param {*} value
 * @returns {boolean}
 */
export function looksLikeHtml(value) {
  if (typeof value !== 'string' || value === '') return false;
  return /<\/?[a-z][a-z0-9]*(\s[^<>]*)?\/?>/i.test(value);
}
